import { useMemo } from "react";
import { resolveBreakdownDimensions } from "../../Helper/Analytics/breakdownDimensions";
import { useOrgLabels, usePagesContent } from "../../Hooks/usePagesContent";

/**
 * The FleetPerformance org meta keys under metaInfo.FleetPerformance.charts.
 *
 * One key per breakdown chart, named after the chart the way the keys under
 * metaInfo.FleetPerformance.table are named after their table, so an org can
 * give each chart its own menu - the Range tab and the Energy Consumption tab
 * are looked at by different people and do not have to agree.
 */
export const FLEET_PERFORMANCE_CHART_KEYS = {
  ENERGY_CONSUMPTION_BREAKDOWN: "energyConsumptionBreakdownChart",
  ENERGY_FLOW_BREAKDOWN: "energyFlowBreakdownChart",
  STATE_OF_USE_BREAKDOWN: "stateOfUseBreakdownChart",
  FLEET_RANGE_DISTRIBUTION: "fleetRangeDistributionChart",
};

/**
 * Every dimension the Fleet Performance breakdown charts can group by.
 *
 * The values are the analytics API's own field names: each one is sent as the
 * groupBy and, when the user picks items in the sidebar, as the drilldown
 * filter's name. A value that is not on this list cannot be configured, because
 * the server's groupBy and filter builders are if/elif chains with no else - an
 * unknown field would group by nothing and drill into an unfiltered set.
 */
export const FLEET_BREAKDOWN_DIMENSIONS = [
  { value: "originCity", label: "By City" },
  { value: "originState", label: "By State" },
  { value: "originDepot", label: "By Depot" },
  { value: "mfgYear", label: "By Onboarding Year" },
  { value: "fleetName", label: "By Customer Account" },
  { value: "fleetCategory", label: "By Business Segment" },
];

/**
 * The menu an org that configures nothing gets: exactly what these four charts
 * have always shown, in the order they showed it.
 *
 * The two customer dimensions are opt-in here, unlike BMS Faults and Smart
 * Alerts where they ship in the menu, so turning the block on cannot change a
 * chart nobody asked to change.
 */
export const FLEET_DEFAULT_BREAKDOWN_DIMENSION_VALUES = [
  "originCity",
  "originState",
  "originDepot",
  "mfgYear",
];

const DEFAULT_AS_CONFIGURED = FLEET_DEFAULT_BREAKDOWN_DIMENSION_VALUES.map((value) => ({
  value,
}));

export const FLEET_BREAKDOWN_GROUP_BY_FALLBACK = "originCity";

/**
 * The dimensions a chart offers, from org meta where the org configures them.
 *
 * The defaults are handed to the shared resolver as if they were the configured
 * list, so the default menu and a real config are filtered, ordered and labelled
 * by one code path. That is also why an absent block - or one naming only fields
 * this module cannot group by - lands on the four defaults rather than on the
 * whole catalogue, which is what the shared resolver alone would do.
 */
export const resolveFleetBreakdownDimensions = (configured, labelOverrides = {}) => {
  const requested = (Array.isArray(configured) ? configured : []).filter((dimension) =>
    FLEET_BREAKDOWN_DIMENSIONS.some((known) => known.value === dimension?.value)
  );

  return resolveBreakdownDimensions(
    requested.length ? requested : DEFAULT_AS_CONFIGURED,
    FLEET_BREAKDOWN_DIMENSIONS,
    labelOverrides
  );
};

/**
 * The backend field a selected dimension groups and filters by.
 *
 * Identity for every dimension the catalogue lists, because on this module the
 * menu values ARE the API's field names. It replaces a four-entry map that each
 * chart declared twice - once per fetcher - where a dimension added to the menu
 * but missed in one copy fell through to originCity and quietly charted the
 * wrong grouping. The fallback now only catches a groupBy that is not a
 * dimension at all.
 */
export const resolveFleetGroupByField = (groupBy) =>
  FLEET_BREAKDOWN_DIMENSIONS.some((dimension) => dimension.value === groupBy)
    ? groupBy
    : FLEET_BREAKDOWN_GROUP_BY_FALLBACK;

/**
 * A chart's breakdown dimensions, resolved against FleetPerformance org meta.
 *
 * The customer dimensions take their names from the same globalConfig.labels row
 * that names them everywhere else, so renaming the customer renames these menu
 * entries with it.
 *
 * Memoized on the ONE chart's configured block, not on the FleetPerformance
 * block or on pagesContent: the page starts at { mounted: false } and swaps in
 * its meta when the fetch lands, so depending on the container would hand back a
 * new array to every org at that moment. These lists build the fetchers that
 * become config.onFetchSidebarData, which useFleetBreakdownData's effects depend
 * on, so a new identity is a duplicate request for a menu that did not change.
 */
export const useFleetBreakdownDimensions = (chartKey) => {
  const pagesContent = usePagesContent();
  const { fleetLabel, fleetCategoryLabel } = useOrgLabels();

  const configured = chartKey
    ? pagesContent?.metaInfo?.FleetPerformance?.charts?.[chartKey]?.breakdownDimensions
    : undefined;

  return useMemo(
    () =>
      resolveFleetBreakdownDimensions(configured, {
        fleetName: `By ${fleetLabel}`,
        fleetCategory: `By ${fleetCategoryLabel}`,
      }),
    [configured, fleetLabel, fleetCategoryLabel]
  );
};
